import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";

export function useTargets(storeCode) {
  const [targets, setTargets] = useState([]);
  const [subtypes, setSubtypes] = useState([]);
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    try {
      const [t, s, l] = await Promise.all([
        api.get("/targets"),
        api.get("/subtypes"),
        api.get("/leads", { params: storeCode ? { store_code: storeCode } : {} }),
      ]);
      setTargets(t.data);
      setSubtypes(s.data);
      setLeads(l.data);
    } catch {
      toast.error("Failed to load targets");
    } finally {
      setLoading(false);
    }
  }, [storeCode]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const isGlobalTarget = (t) => t.store_code == null;
  const isGlobalSubtype = (s) => s.store_code == null;

  const err = (e, msg) => toast.error(formatApiErrorDetail(e.response?.data?.detail) || msg);

  const addTarget = async (name, icon) => {
    try {
      const { data } = await api.post("/targets", { name, icon, store_code: storeCode || null });
      setTargets((p) => [...p, data]);
      toast.success("Target added");
      return data;
    } catch (e) { err(e, "Failed to add target"); return null; }
  };
  const updateTarget = async (id, name, icon) => {
    try {
      await api.put(`/targets/${id}`, { name, icon });
      setTargets((p) => p.map((t) => (t.id === id ? { ...t, name, icon } : t)));
      toast.success("Target updated");
      return true;
    } catch (e) { err(e, "Failed to update target"); return false; }
  };
  const deleteTarget = async (id) => {
    try {
      await api.delete(`/targets/${id}`);
      setTargets((p) => p.filter((t) => t.id !== id));
      setSubtypes((p) => p.filter((s) => s.target_id !== id));
      setLeads((p) => p.filter((l) => l.target_id !== id));
      toast.success("Target deleted");
      return true;
    } catch (e) { err(e, "Failed to delete target"); return false; }
  };

  const addSubtype = async (targetId, name) => {
    try {
      const { data } = await api.post("/subtypes", { target_id: targetId, name, store_code: storeCode || null });
      setSubtypes((p) => [...p, data]);
      toast.success("Sub-type added");
      return data;
    } catch (e) { err(e, "Failed to add sub-type"); return null; }
  };
  const updateSubtype = async (id, name) => {
    try {
      await api.put(`/subtypes/${id}`, { name });
      setSubtypes((p) => p.map((s) => (s.id === id ? { ...s, name } : s)));
      toast.success("Sub-type updated");
      return true;
    } catch (e) { err(e, "Failed to update sub-type"); return false; }
  };
  const deleteSubtype = async (id) => {
    try {
      await api.delete(`/subtypes/${id}`);
      setSubtypes((p) => p.filter((s) => s.id !== id));
      toast.success("Sub-type deleted");
      return true;
    } catch (e) { err(e, "Failed to delete sub-type"); return false; }
  };

  const addLead = async (lead) => {
    try {
      const { data } = await api.post("/leads", { ...lead, store_code: storeCode || lead.store_code || null });
      setLeads((p) => [data, ...p]);
      toast.success("Contact added");
      return data;
    } catch (e) { err(e, "Failed to add contact"); return null; }
  };
  const updateLead = async (id, updates) => {
    try {
      const existing = leads.find((l) => l.id === id) || {};
      const payload = { ...existing, ...updates };
      delete payload.id; delete payload.created_at; delete payload.updated_at;
      const { data } = await api.put(`/leads/${id}`, payload);
      setLeads((p) => p.map((l) => (l.id === id ? data : l)));
      toast.success("Contact updated");
      return true;
    } catch (e) { err(e, "Failed to update contact"); return false; }
  };
  const deleteLead = async (id) => {
    try {
      await api.delete(`/leads/${id}`);
      setLeads((p) => p.filter((l) => l.id !== id));
      toast.success("Contact deleted");
      return true;
    } catch (e) { err(e, "Failed to delete contact"); return false; }
  };

  const getSubtypesForTarget = (targetId) => subtypes.filter((s) => s.target_id === targetId);
  const getLeadsForTarget = (targetId, subtypeId) =>
    leads.filter((l) => l.target_id === targetId && (!subtypeId || l.subtype_id === subtypeId));

  return {
    targets, subtypes, leads, loading,
    addTarget, updateTarget, deleteTarget,
    addSubtype, updateSubtype, deleteSubtype,
    addLead, updateLead, deleteLead,
    onAddTarget: addTarget, onUpdateTarget: updateTarget, onDeleteTarget: deleteTarget,
    onAddSubtype: addSubtype, onUpdateSubtype: updateSubtype, onDeleteSubtype: deleteSubtype,
    onAddLead: addLead, onUpdateLead: updateLead, onDeleteLead: deleteLead,
    getSubtypesForTarget, getLeadsForTarget,
    isGlobalTarget, isGlobalSubtype, refetch: fetchData,
  };
}
